import React, { useEffect, useState } from "react"
import { Link } from "gatsby"
import styled, { css } from "styled-components"
import { Lockup } from "./logo"
import { WHATSAPP_LINK } from "../config/contact"

const Bar = styled.header`
  position: sticky;
  top: 0;
  z-index: 1000;
  background: ${({ theme }) => theme.colors.dark};
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderOnDark};
  transition: padding ${({ theme }) => theme.transitions.normal};
  padding: ${({ $compact }) => ($compact ? "12px 0" : "20px 0")};
`

const Container = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 32px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;

  @media (max-width: 768px) {
    padding: 0 20px;
  }
`

const LogoLink = styled(Link)`
  display: inline-flex;
  align-items: center;
`

const Nav = styled.nav`
  display: flex;
  align-items: center;
  gap: 28px;

  @media (max-width: 768px) {
    display: ${({ $open }) => ($open ? "flex" : "none")};
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    flex-direction: column;
    align-items: flex-start;
    background: ${({ theme }) => theme.colors.dark};
    border-bottom: 1px solid ${({ theme }) => theme.colors.borderOnDark};
    padding: 18px 24px 24px;
    gap: 16px;
  }
`

const linkStyles = css`
  font-family: ${({ theme }) => theme.fonts.body};
  font-size: 13px;
  font-weight: 400;
  letter-spacing: 0;
  color: ${({ theme }) => theme.colors.textOnDarkMuted};
  position: relative;
  transition: color ${({ theme }) => theme.transitions.fast};

  &:hover, &.active {
    color: ${({ theme }) => theme.colors.textOnDark};
  }
`

const NavAnchor = styled.a`${linkStyles}`

const HeaderCTA = styled.a`
  display: inline-flex;
  align-items: center;
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: 11px;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.dark};
  background: ${({ theme }) => theme.colors.paper};
  padding: 10px 16px;
  border-radius: 2px;
  transition: background ${({ theme }) => theme.transitions.fast},
    transform ${({ theme }) => theme.transitions.fast};

  &:hover {
    background: #FFFFFF;
    transform: translateY(-1px);
  }

  @media (max-width: 768px) {
    display: none;
  }
`

const MenuButton = styled.button`
  display: none;
  font-size: 1.5rem;
  color: ${({ theme }) => theme.colors.textOnDark};

  @media (max-width: 768px) {
    display: block;
  }
`

const NAV_LINKS = [
  { label: "Product", href: "#product" },
  { label: "Architecture", href: "#architecture" },
  { label: "Capabilities", href: "#capabilities" },
  { label: "Audience", href: "#audience" },
]

const Header = () => {
  const [compact, setCompact] = useState(false)
  const [open, setOpen] = useState(false)

  useEffect(() => {
    const onScroll = () => setCompact(window.scrollY > 32)
    window.addEventListener("scroll", onScroll, { passive: true })
    return () => window.removeEventListener("scroll", onScroll)
  }, [])

  return (
    <Bar $compact={compact}>
      <Container>
        <LogoLink to="/" aria-label="VaultBridge — Home">
          <Lockup
            size={36}
            height={20}
            ink="#F2F4F7"
            accent="#7B8FA8"
            descriptor={false}
            gap={14}
          />
        </LogoLink>

        <MenuButton
          aria-label={open ? "Close menu" : "Open menu"}
          onClick={() => setOpen(o => !o)}
        >
          {open ? "✕" : "☰"}
        </MenuButton>

        <Nav $open={open}>
          {NAV_LINKS.map(link => (
            <NavAnchor
              key={link.href}
              href={link.href}
              onClick={() => setOpen(false)}
            >
              {link.label}
            </NavAnchor>
          ))}
        </Nav>

        <HeaderCTA
          href={WHATSAPP_LINK}
          target="_blank"
          rel="noopener noreferrer"
        >
          Book a demo
        </HeaderCTA>
      </Container>
    </Bar>
  )
}

export default Header
